import { useQuery } from '@tanstack/react-query'
import { usePlatformCompetitionRequests } from '@/features/platform/api'
import {
  registrationAnswersQueryOptions,
  registrationFormsQueryOptions,
  teamStatusQueryOptions,
} from './query-options'

/** Single-competition convenience wrappers over `query-options.ts` — kept for reuse/testing outside the `useQueries` fan-out in `use-home-overview.ts`, which cannot call these as hooks in a loop. */
export function useMyTeamStatus(competitionId: string) {
  return useQuery(teamStatusQueryOptions(competitionId))
}

export function useMyRegistrationFormStatus(competitionId: string): {
  hasForm: boolean
  completed: boolean
} {
  const forms = useQuery(registrationFormsQueryOptions(competitionId))
  const form = forms.data?.[0]
  const hasForm = form !== undefined && form.currentVersion !== null
  const answers = useQuery(
    registrationAnswersQueryOptions(competitionId, hasForm ? form.id : undefined),
  )
  return {
    hasForm,
    completed: !hasForm || answers.data != null,
  }
}

/**
 * P2 "N 個競賽申請等待審核" — same two calls the `/platform` dashboard's own
 * "Action Needed" section makes (`platform-dashboard-page.tsx`), replicated
 * here rather than imported from that route so `/home` never depends on
 * `/platform`'s internals (out of scope for this task).
 * `changes_requested` is excluded — the ball is with the applicant, not the
 * platform admin, so it is not "my next step".
 */
export function usePendingCompetitionRequestCount(enabled: boolean): number {
  const submitted = usePlatformCompetitionRequests(
    { status: 'submitted' },
    { limit: 1, offset: 0 },
    enabled,
  )
  const underReview = usePlatformCompetitionRequests(
    { status: 'under_review' },
    { limit: 1, offset: 0 },
    enabled,
  )
  return (submitted.data?.total ?? 0) + (underReview.data?.total ?? 0)
}
